import { all, put, call, getContext, takeLatest } from 'redux-saga/effects';

import { Logger } from 'utils/Logger';
import { ProductTypeTitle, ProductType } from 'enums/product-type';

import {
  lenderProductsStatus,
  lenderToggleStatusBySection,
  lenderProduct,
  updateLenderProduct,
  lenderProductOffer,
} from './types';

import {
  requestLenderProductsStatusSuccess,
  requestLenderProductsStatusFailure,
  requestLenderProductSuccess,
  requestLenderProductFailure,
  requestLenderProductOfferSuccess,
  requestLenderProductOfferFailure,
} from './actions';

const logger = new Logger('Saga:Lender');

export function* requestLenderProductsStatusSaga(action) {
  const routerService = yield getContext('routerService');
  const authApiService = yield getContext('authApiService');

  try {
    const response = yield call(
      authApiService.get,
      routerService.generate('api.lender_criteria_summary'),
    );

    const activeSections = response.data.reduce((list, current) => {
      if (current.active === true) {
        list.push(current.productType);
      }
      return list;
    }, []);

    yield put(requestLenderProductsStatusSuccess(activeSections));
    action.promise && action.promise.resolve(activeSections);
  } catch (error) {
    logger.error('Failed to get the lender products: ', error);
    yield put(requestLenderProductsStatusFailure(error));
    action.promise && action.promise.reject(error);
  }
}

export function* requestLenderToggleStatusBySectionSaga(action) {
  const routerService = yield getContext('routerService');
  const authApiService = yield getContext('authApiService');
  const section = ProductTypeTitle[action.section] || ProductTypeTitle[ProductType[action.section]];

  try {
    yield call(
      authApiService.patch,
      routerService.generate('api.criteria_product_status', { section }),
      { active: action.checked },
    );
  } catch (error) {
    logger.error(`Failed to get change product status ${action.section}: `, error);
    yield put(requestLenderProductsStatusFailure(error));
  }
}

export function* requestLenderProductSaga(action) {
  const routerService = yield getContext('routerService');
  const authApiService = yield getContext('authApiService');
  const section = ProductTypeTitle[action.section] || ProductTypeTitle[ProductType[action.section]];

  try {
    const response = yield call(
      authApiService.get,
      routerService.generate('api.criteria_product', { section }),
    );

    yield put(requestLenderProductSuccess(action.section, response.data.forms));
  } catch (error) {
    logger.error(`Failed to get the ${action.section}: `, error);
    yield put(requestLenderProductFailure(action.section, error));
  }
}

export function* requestUpdateLenderProductSaga(action) {
  const routerService = yield getContext('routerService');
  const authApiService = yield getContext('authApiService');
  const section = ProductTypeTitle[action.section] || ProductTypeTitle[ProductType[action.section]];

  try {
    const payload =
      action.formTab === 'lender_criteria_product_fees'
        ? { fees: action.data }
        : { constraints: action.data };

    const response = yield call(
      authApiService.put,
      routerService.generate('api.criteria_product', { section }),
      payload,
    );

    yield put(requestLenderProductSuccess(response.data.forms));
  } catch (error) {
    logger.error(`Failed to get the ${section} in tab ${action.formTab}: `, error);
    yield put(requestLenderProductFailure(error));
  }
}

export function* requestLenderProductOfferSaga(action) {
  const authApiService = yield getContext('authApiService');
  const routerService = yield getContext('routerService');
  const { fundingMatchId, fundingSearchId } = action;

  try {
    const response = yield call(
      authApiService.get,
      routerService.generate('api.platform.funding_match_offer_schema', {
        fundingMatchId,
        fundingSearchId,
      }),
    );

    yield put(requestLenderProductOfferSuccess(response.data));
  } catch (error) {
    logger.error('Failed to request lender product offer with the following error', error);
    yield put(requestLenderProductOfferFailure(error));
  }
}

export default function* lenderSaga() {
  return yield all([
    takeLatest(lenderProductsStatus.request, requestLenderProductsStatusSaga),
    takeLatest(lenderToggleStatusBySection.request, requestLenderToggleStatusBySectionSaga),
    takeLatest(lenderProduct.request, requestLenderProductSaga),
    takeLatest(updateLenderProduct.request, requestUpdateLenderProductSaga),
    takeLatest(lenderProductOffer.request, requestLenderProductOfferSaga),
  ]);
}
